// 全局错误监控和处理工具

// 错误类型枚举
export const ErrorType = {
  HTTP: 'http',
  VUE: 'vue',
  JS: 'javascript',
  RESOURCE: 'resource',
  PROMISE: 'promise'
}

// 错误级别枚举
export const ErrorLevel = {
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error',
  FATAL: 'fatal'
}

// 错误日志记录
const errorLogs = []

// 最大日志数量
const MAX_LOGS = 100

// 错误处理配置
const config = {
  // 是否开启错误上报
  enableReport: false, // 暂时禁用错误上报
  // 上报地址（根据实际情况修改）
  reportUrl: '/api/error/report',
  // 错误过滤函数
  filterError: (error) => {
    // 过滤掉开发环境的某些错误
    if (process.env.NODE_ENV === 'development') {
      // 过滤掉Element Plus的一些已知警告
      if (error.message && error.message.includes('Element Plus')) {
        return false
      }
    }

    // 过滤掉"用户不存在"的错误，这是正常的业务逻辑错误
    if (error.message && error.message.includes('用户不存在')) {
      return false
    }

    return true
  },
  // 错误采样率（0-1）
  sampleRate: 1.0
}

// 格式化错误信息
function formatError (error, type, level = ErrorLevel.ERROR, extra = {}) {
  const errorInfo = {
    type,
    level,
    message: error.message || 'Unknown error',
    stack: error.stack || '',
    timestamp: Date.now(),
    url: window.location.href,
    userAgent: navigator.userAgent,
    referer: document.referrer,
    // 自定义额外信息
    extra: {
      ...extra,
      // 添加用户信息（根据实际情况从store中获取）
      userId: null,
      userName: null
    }
  }

  // 特殊处理HTTP错误
  if (type === ErrorType.HTTP) {
    errorInfo.request = {
      url: error.config?.url,
      method: error.config?.method,
      params: error.config?.params,
      data: error.config?.data
    }
    errorInfo.response = {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data
    }
  }

  return errorInfo
}

// 添加错误日志
export function addErrorLog (error, type, level, extra) {
  const errorInfo = formatError(error, type, level, extra)

  // 过滤错误
  if (!config.filterError(errorInfo)) {
    return
  }

  // 添加到日志队列
  errorLogs.push(errorInfo)

  // 限制日志数量
  if (errorLogs.length > MAX_LOGS) {
    errorLogs.shift()
  }

  // 打印错误日志（开发环境）
  if (process.env.NODE_ENV === 'development') {
    console.error(`[Error Monitor] ${errorInfo.type} Error:`, errorInfo)
  }

  // 上报错误
  if (config.enableReport && Math.random() <= config.sampleRate && process.env.NODE_ENV === 'production') {
    reportError(errorInfo)
  }

  return errorInfo
}

// 上报错误
async function reportError (errorInfo) {
  try {
    // 模拟上报，实际项目中替换为真实上报逻辑
    if (config.enableReport && process.env.NODE_ENV === 'production') {
      // 使用fetch上报，避免依赖axios
      await fetch(config.reportUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(errorInfo),
        // 避免跨域问题
        credentials: 'include'
      })
    }
  } catch (reportError) {
    console.error('[Error Monitor] Report failed:', reportError)
  }
}

// 获取错误日志
export function getErrorLogs () {
  return [...errorLogs]
}

// 清除错误日志
export function clearErrorLogs () {
  errorLogs.length = 0
}

// 初始化全局错误监控
export function initErrorHandler (app, store) {
  // 1. Vue错误处理
  app.config.errorHandler = (err, vm, info) => {
    addErrorLog(err, ErrorType.VUE, ErrorLevel.ERROR, {
      component: vm?.$options?.name || 'unknown',
      lifecycleHook: info
    })
  }

  // 2. JavaScript运行时错误
  window.addEventListener('error', (event) => {
    // 过滤掉资源加载错误，单独处理
    if (event.target instanceof HTMLElement) {
      return
    }

    addErrorLog(event.error, ErrorType.JS, ErrorLevel.ERROR, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno
    })
  })

  // 3. 资源加载错误
  window.addEventListener('error', (event) => {
    // 只处理资源加载错误
    if (event.target instanceof HTMLElement) {
      addErrorLog(
        new Error(`Resource load failed: ${event.target.src || event.target.href}`),
        ErrorType.RESOURCE,
        ErrorLevel.WARN,
        {
          resourceType: event.target.tagName.toLowerCase(),
          resourceUrl: event.target.src || event.target.href
        }
      )
    }
  }, true)

  // 4. Promise未捕获错误
  window.addEventListener('unhandledrejection', (event) => {
    addErrorLog(event.reason, ErrorType.PROMISE, ErrorLevel.ERROR)
    event.preventDefault()
  })

  // 5. Axios错误处理（在request.js中使用）

  // 6. 更新配置
  if (store) {
    // 从store中获取用户信息
    const updateUserInfo = () => {
      config.filterError = (error) => {
        // 更新用户信息
        error.extra.userId = store.state.user?.userInfo?.id || store.state.user?.userInfo?.userId || store.state.user?.userInfo?.phone
        error.extra.userName = store.state.user?.userInfo?.username
        return true
      }
    }

    updateUserInfo()

    // 监听用户登录状态变化
    store.watch(
      (state) => state.user.isLogin,
      (isLogin) => {
        if (isLogin) {
          updateUserInfo()
        }
      }
    )
  }
}

// 获取用户友好的错误信息
export function getFriendlyErrorMessage (error, defaultMsg = '操作失败，请稍后重试') {
  if (!error) return defaultMsg

  // 检查是否是axios错误对象
  if (error.isAxiosError) {
    // Axios错误处理
    if (error.response) {
      // 服务器响应了错误状态码
      const { status, data, statusText } = error.response
      
      // 优先使用后端返回的错误信息
      if (data?.msg || data?.message) {
        return data.msg || data.message
      }
      
      // 根据状态码返回友好信息
      switch (status) {
        case 400: 
          console.warn(`HTTP 400错误详情:`, data || statusText)
          return '请求参数错误'
        case 401: 
          console.warn('HTTP 401错误: 未授权访问')
          return '请先登录'
        case 403: 
          console.warn('HTTP 403错误: 无权限访问')
          return '没有操作权限'
        case 404: 
          console.warn(`HTTP 404错误: 请求的资源不存在 - ${error.response.config?.url}`)
          return '请求的资源不存在'
        case 405: 
          console.warn('HTTP 405错误: 请求方法不允许')
          return '请求方法不允许'
        case 500: 
          console.error(`HTTP 500错误详情:`, data || statusText)
          return '服务器内部错误'
        case 502: 
          console.error('HTTP 502错误: 网关错误')
          return '网关错误'
        case 503: 
          console.error('HTTP 503错误: 服务器繁忙')
          return '服务器繁忙，请稍后重试'
        case 504: 
          console.error('HTTP 504错误: 请求超时')
          return '请求超时'
        default: 
          console.warn(`HTTP ${status}错误:`, data || statusText)
          return `${status}错误，请稍后重试`
      }
    } else if (error.request) {
      // 请求已发出但没有收到响应（网络错误、超时等）
      console.error('网络请求错误:', error.message || error.code)
      if (error.code === 'ECONNABORTED') {
        return '请求超时，请稍后重试'
      } else if (error.code === 'ENOTFOUND') {
        return '无法连接到服务器，请检查网络'
      } else {
        return '网络连接失败，请检查网络设置'
      }
    } else {
      // 其他请求错误
      console.error('请求配置错误:', error.message)
      return error.message || defaultMsg
    }
  } else if (error.response) {
    // 非Axios错误但有response属性
    const { status, data } = error.response

    // 优先使用后端返回的错误信息
    if (data?.msg) {
      return data.msg
    }

    // 根据状态码返回友好信息
    switch (status) {
      case 400: return '请求参数错误'
      case 401: return '请先登录'
      case 403: return '没有操作权限'
      case 404: return '请求的资源不存在'
      case 500: return '服务器内部错误'
      case 502: return '网关错误'
      case 503: return '服务器繁忙，请稍后重试'
      case 504: return '请求超时'
      default: return `${status}错误，请稍后重试`
    }
  }

  // 网络错误
  if (error.message && error.message.includes('Network Error')) {
    console.error('网络错误:', error.message)
    return '网络连接失败，请检查网络设置'
  }

  // 超时错误
  if (error.message && (error.message.includes('timeout') || error.code === 'ECONNABORTED')) {
    console.warn('超时错误:', error.message)
    return '请求超时，请稍后重试'
  }

  // 其他错误
  return error.message || defaultMsg
}

// 导出默认错误处理器
export default {
  init: initErrorHandler,
  addLog: addErrorLog,
  getLogs: getErrorLogs,
  clearLogs: clearErrorLogs,
  getFriendlyMessage: getFriendlyErrorMessage,
  ErrorType,
  ErrorLevel
}